import React, { createContext, useContext, useEffect, useState, useRef } from "react";
import { useEventContext } from "./EventContext";
import { getStreamRecordStatus, getFilteredStreams } from "../helpers/stream-helper";
import { participationTypeFilter } from "./UserContext";
import { usePlayerContext } from "./PlayerContext";
import { useWidgetContext } from "./WidgetContextProvider";

export const StreamContext = createContext(null);

export const useStreamContext = () => useContext(StreamContext);

export const StreamContextProvider = ({ children }) => {
    const { event } = useEventContext();
    const { widgetSettings } = useWidgetContext();
    const { setStream: setPlayerStream } = usePlayerContext();
    const defaultParticipationType = localStorage.getItem(`participationType_${event?.eventKey}`) || '';
    const [participationType, setParticipationType] = useState(participationTypeFilter(defaultParticipationType))
    const [stream, updateStream] = useState(null);
    const [streams, setStreams] = useState([]);
    const [recordStatus, setRecordStatus] = useState(null);
    const [widgetEpisode, setWidgetEpisode] = useState(null);
    const streamsRef = useRef(streams);

    const setStream = (stream) => {
        updateStream(stream);
        setRecordStatus(getStreamRecordStatus(stream));
    }

    useEffect(() => {
        if (widgetEpisode) {
            streams.forEach(stream => {
                if (stream.episode?.length) {
                    const { idType, id } = widgetEpisode;
                    const targetEpisode = stream.episode.find((ep) => String(ep[idType]) === String(id));
                    if (targetEpisode) {
                        setStream(stream);
                        setPlayerStream(stream, targetEpisode)
                    }
                }

            })
            setWidgetEpisode(null);
        }
    }, [widgetEpisode, streams])

    useEffect(() => {
        const eventStreams = getFilteredStreams(event?.stream, participationType, widgetSettings);
        setStreams(eventStreams);
        streamsRef.current = eventStreams;

        if (!stream || !eventStreams.map(({ id }) => id).includes(stream?.id)) {
            setStream(eventStreams[0]);
        } else {
            setStream(eventStreams.find(({ id }) => id === stream?.id));
        }
    }, [event, widgetSettings, participationType]);

    useEffect(() => {
        const messageHandler = (event) => {
            try {
                const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
                if (data?.hasOwnProperty('setEpisode')) {
                    setWidgetEpisode({ idType: 'id', id: data.setEpisode });
                }
                if (data?.hasOwnProperty('setEpisodeByExternalId')) {
                    setWidgetEpisode({ idType: 'externalId', id: data.setEpisodeByExternalId });
                }
                if (data?.hasOwnProperty('setStream')) {
                    const currentStream = streamsRef.current.find(({ id }) => id === data?.setStream);
                    setStream(currentStream);
                }
            } catch (error) {
                console.error(error);
            }
        };

        window.addEventListener('message', messageHandler);

        return () => {
            window.removeEventListener('message', messageHandler);
        }
    }, []);

    const context = {
        streams,
        stream,
        recordStatus,
        setStream,
        setParticipationType
    };

    return (
        <StreamContext.Provider value={context}>
            {children}
        </StreamContext.Provider>
    )
}
